'use client';

import React, { useEffect, useState } from 'react';
import { yupFormResolver } from '@efacity/react-hook-form-mui';
import { PaymentFormElements, WithLoading } from '@efacity/frontend-shared';
import { EnrollmentFormValues, moneyWithCurrencyCodeFormatter } from '@efacity/common';
import { FormProvider, useForm } from 'react-hook-form';
import { cloneDeep } from 'lodash';
import { OrganizationInformation } from '@efacity/react-next-sc';
import { useEnrollmentApplication } from './useEnrollmentApplication';
import { enrollmentWithPaymentFormValidationSchema } from './paymentValidationSchema';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { EnrollmentForm } from './EnrollmentForm';
import { Box, Grid, Typography } from '@mui/material';

interface EnrollmentFormProps {
  orgInfo: OrganizationInformation;
  enrollmentApplication: ReturnType<typeof useEnrollmentApplication>;
}

export const EnrollmentFormContainerWithStripe: React.FC<EnrollmentFormProps> = ({
  orgInfo,
  enrollmentApplication
}) => {
  const {
    enrollmentApplicationFormState: {
      isLoading,
      mergedFormValues,
      stripePublishableKey,
      paymentRequired,
      price,
      currency,
      paymentCards
    }
  } = enrollmentApplication;
  const [stripe, setStripe] = useState(null);
  const [isLoadingStripe, setIsLoadingStripe] = useState(true);

  const methods = useForm<EnrollmentFormValues>({
    resolver: paymentRequired
      ? yupFormResolver<EnrollmentFormValues>(enrollmentWithPaymentFormValidationSchema)
      : undefined,
    defaultValues: cloneDeep(mergedFormValues),
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUseNativeValidation: false,
    mode: 'onTouched'
  });

  useEffect(() => {
    methods.reset(cloneDeep(mergedFormValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(mergedFormValues)]);

  useEffect(() => {
    // when publishableKey changes, get new stripe object
    // set it and then pass to Elements
    const getStripe = async () => {
      try {
        const stripe = await loadStripe(stripePublishableKey);
        setStripe(stripe);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ERR', error);
      } finally {
        setIsLoadingStripe(false);
      }
    };

    if (stripePublishableKey && paymentRequired) {
      setIsLoadingStripe(true);
      getStripe();
    } else {
      setStripe(null);
    }
  }, [stripePublishableKey, paymentRequired]);

  const paymentSection = paymentRequired ? (
    <WithLoading isLoading={isLoading || isLoadingStripe} message={'Loading Stripe...'} style={{ width: '25%' }}>
      <Grid size={{ xs: 12 }} style={{ marginTop: 15, marginBottom: 15 }}>
        <Grid container>
          <Grid size={{ sm: 2, md: 3, lg: 3 }} />
          <Grid size={{ xs: 12, sm: 8, md: 6, lg: 6 }}>
            {stripePublishableKey && stripe ? (
              <>
                {(price || 0) > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: '500',
                        fontSize: 18,
                        textAlign: 'right',
                        color: 'primary.main'
                      }}
                    >
                      {`Application fee: ${moneyWithCurrencyCodeFormatter(price, currency)}`}
                    </Typography>
                  </Box>
                )}
                <PaymentFormElements pathToObject={'payment'} paymentCards={paymentCards} />
              </>
            ) : (
              <div style={{ fontSize: 20, color: '#f44336', textAlign: 'center', margin: 30 }}>
                Failed to load Stripe info. Unable to process payment.
              </div>
            )}
          </Grid>
        </Grid>
      </Grid>
    </WithLoading>
  ) : null;

  return (
    <Elements stripe={stripe}>
      <FormProvider {...methods}>
        <EnrollmentForm
          orgInfo={orgInfo}
          enrollmentApplication={enrollmentApplication}
          paymentSection={paymentSection}
        />
      </FormProvider>
    </Elements>
  );
};
